<script lang="ts">
	import type { MaterialProperty, MaterialPropertyListElement } from './types/materialProperty'
	import {
		DefaultMaterialProperty,
		DefaultMaterialPropertyListElement,
		MaterialPropertyView
	} from './types/materialProperty'
	import MaterialPropertyListElementForm from './MaterialPropertyListElementForm.svelte'
	import Breadcrumbs from '#lib/organisms/Breadcrumbs.svelte'
	import type { Breadcrumb } from '#lib/organisms/Breadcrumbs/breadcrumbs.svelte.js'
	import MaterialPropertyForm from './MaterialPropertyForm.svelte'
	import { t } from '$i18n/i18n'
	import { TooltipPlacements } from '$types/enums'
	import { closeModalOnOutsideClick, notifications } from '#lib/stores.js'
	import { breadcrumbs } from '#lib/organisms/Breadcrumbs/breadcrumbs.svelte.js'
	import { getContext, hasContext, onMount, setContext, untrack } from 'svelte'
	import { on } from 'svelte/events'
	import { modals, onBeforeClose } from 'svelte-modals'
	import { fly } from 'svelte/transition'
	import Button from '#lib/atoms/Button.svelte'
	import Notifications from '#lib/organisms/Notifications.svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import { writable, type Writable } from 'svelte/store'
	import Tooltip from '#lib/atoms/Tooltip.svelte'
	import { changeUrl } from '$utils/misc'
	import type { Material } from './types/material'
	import {
		MaterialPropertyHandler,
		MaterialPropertyListElementHandler
	} from './util/materialPropertyModalUtil'
	import IconCross from '$svg/icon-cross.svg?component'
	import IconCheckmark from '$svg/icon-checkmark.svg?component'
	import { deepEqual } from '$utils/utils.svelte'
	import { mmpvState } from './mmpv.state.svelte'
	import type { FormError } from '$types/formError.ts'

	/** these props need to be defined when calling modals.open(MaterialPropertyModal, { materialProperty: ..., ... }) */
	type Props = {
		view?: MaterialPropertyView
		/** because we can step further and add breadcrumbs, we have the parent to directly add ids to the parent's list */
		parent: MaterialPropertyListElement | MaterialProperty | Material
		materialProperty: MaterialProperty
		materialId?: string
		updateMaterial?: (propertyId: string) => void
		isNew?: boolean
		isOpen: boolean
		showCloseIcon?: boolean
		acceptDisabled?: boolean
	}

	/**
	 * view, parent and isNew are reassigned by the internal breadcrumb
	 * state machine. no caller binds these props (they are passed once via openModal), so
	 * $bindable() only serves to permit those internal reassignments — there is no writeback.
	 */
	let {
		view = $bindable(MaterialPropertyView.MATERIAL_PROPERTY),
		parent = $bindable(),
		materialProperty,
		materialId = undefined,
		updateMaterial = undefined,
		isNew = $bindable(true),
		isOpen,
		showCloseIcon = false,
		acceptDisabled = false
	}: Props = $props()

	const BREADCRUMBS_SIMPLE_POP_LENGTH = 3
	setContext('useMaterialDesign', true)
	const formErrors: Writable<FormError[]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([])

	let materialPropertyCopy: MaterialProperty = $state({ ...materialProperty })

	let propertyElement: MaterialPropertyListElement = $state(DefaultMaterialPropertyListElement())
	let propertyElementCopy: MaterialPropertyListElement = $state(
		DefaultMaterialPropertyListElement()
	)
	/**
	 * the initializer only provides a typed value for the default view — the
	 * updateViewVariables() call below (before first render) rebuilds the handler
	 * for the actual `view` prop.
	 */
	let viewHandler: MaterialPropertyHandler | MaterialPropertyListElementHandler = $state(
		new MaterialPropertyHandler(
			materialProperty,
			isNew,
			mmpvState.materialPropertyParents,
			mmpvState.materialProperties
		)
	)
	// form component instances, only bound while their view is active
	let materialPropertyForm: MaterialPropertyForm | undefined = $state()
	let propertyElementForm: MaterialPropertyListElementForm | undefined = $state()

	let showOverlay: boolean = $state(false)
	const overlayTitle: string = $t('UI.mmpv.materialPropertyModal.unsavedChanges.title')
	let overlayMessage: string = $state('Nachricht, die wir anzeigen wollen')
	let overlayButtonLabel: string = $state(
		$t('UI.mmpv.materialPropertyModal.unsavedChanges.button.quit')
	)
	let overlayAction: () => void = $state(() => {})

	// snapshot the outside-click setting so onBeforeClose can restore it (this modal
	// disables it on mount; the store is global, so leaving it false leaks to other modals)
	let previousCloseModalOnOutsideClick = false

	onBeforeClose(() => {
		breadcrumbs.clear()
		$formErrors = []
		// by closing modal remove existing notifications
		$notifications = $notifications.filter((notification) => notification.target === null)
		$closeModalOnOutsideClick = previousCloseModalOnOutsideClick
	})

	onMount(() => {
		previousCloseModalOnOutsideClick = $closeModalOnOutsideClick
		$closeModalOnOutsideClick = false
		materialPropertyCopy = { ...materialProperty }
		propertyElementCopy = { ...propertyElement }
		breadcrumbs.push({
			title: viewHandler.breadcrumbTitle,
			data: {
				view: MaterialPropertyView.MATERIAL_PROPERTY,
				materialProperty: { ...materialProperty },
				materialPropertyCopy: { ...materialPropertyCopy },
				parent: parent
			},
			callback: (data, index: number) =>
				changeToMaterialProperty(
					data.materialProperty,
					data.materialPropertyCopy,
					data.parent,
					true,
					index
				)
		})
		/**
		 * the parents tooltip html (built in materialPropertyModalUtil) dispatches this custom
		 * event. `on` hands back an off() function we return as teardown, so the listener no
		 * longer leaks across modal instances (the svelte 4 version never removed it).
		 */
		const off = on(document, 'tooltipRedirect', checkChangesAndRedirectToProperty)
		return () => off()
	})

	function updateViewVariables() {
		switch (view) {
			case MaterialPropertyView.MATERIAL_PROPERTY:
				viewHandler = new MaterialPropertyHandler(
					materialProperty,
					isNew,
					mmpvState.materialPropertyParents,
					mmpvState.materialProperties
				)
				break
			case MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT:
				viewHandler = new MaterialPropertyListElementHandler(propertyElement)
				break
		}
	}

	/**
	 * build viewHandler before the first render — the template reads viewHandler.title
	 * unconditionally. this matches the svelte 4 timing where the `$:` block ran before
	 * mount, whereas the $effect below only runs after the first render.
	 */
	updateViewVariables()

	function save() {
		switch (view) {
			case MaterialPropertyView.MATERIAL_PROPERTY:
				saveMaterialProperty()
				break
			case MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT:
				savePropertyListElement()
				break
		}
	}

	/**
	 * Saves a property list element.
	 * Removes any unnecessary list item and updates the material property.
	 */
	async function savePropertyListElement() {
		if (!propertyElementForm) return // form is always mounted in this view
		const validatedPropertyListElement: MaterialPropertyListElement | null =
			await propertyElementForm.saveMaterialPropertyListElement(propertyElement)
		if (validatedPropertyListElement !== null) {
			breadcrumbs.pop() // pop the list item, which we do not need anymore
			const topElement = breadcrumbs.pop() //pop the property to get the data
			if (!topElement) return

			breadcrumbs.push(topElement) // re-add the property to have the correct breadcrumbs
			const data = topElement.data // get the data of the topElement, so the materialProperty
			data.materialProperty.propertyListElements =
				data.materialProperty.propertyListElements.filter(
					(propertyListElement: MaterialPropertyListElement) =>
						propertyListElement.id !== validatedPropertyListElement.id
				) // remove old PropertyListElement
			data.materialProperty.propertyListElements.push(validatedPropertyListElement) // replace it with the new PropertyListElement
			changeToMaterialProperty(data.materialProperty, data.materialPropertyCopy, data.parent, false)
		}
	}

	/**
	 * Saves a material property.
	 * If the property was saved with material context, it adds the property to the material.
	 * Handles the breadcrumb for different conditions.
	 */
	async function saveMaterialProperty() {
		if (!materialPropertyForm) return // form is always mounted in this view
		const identifier = 'modal'
		let id = await materialPropertyForm.saveMaterialProperty(
			materialProperty,
			identifier,
			materialId
		)
		if (id !== undefined && id !== null) {
			// do breadcrumbs things or add property to material to be able to save it
			if (breadcrumbs.get().length >= BREADCRUMBS_SIMPLE_POP_LENGTH) {
				// we are at least in prop A > listItem > prop B
				breadcrumbs.pop() // we pop this property, because we saved it already
				// restore the old data
				const topElement: Breadcrumb | undefined = breadcrumbs.pop()
				const prevProperty: Breadcrumb | undefined = breadcrumbs.pop() // we need the data of the older property to be able to add the list element to it
				if (!topElement || !prevProperty) return // cannot happen, length was checked above
				// we do not want to remove the breadcrumb completely, just get the data
				breadcrumbs.push(prevProperty)
				breadcrumbs.push(topElement)
				const data = topElement.data
				if (parent && 'propertyChildIds' in parent) {
					// the parent is a listElement
					if (!parent.propertyChildIds.includes(id)) {
						parent.propertyChildIds.push(id)
					}
					const parentPropertyId = parent.propertyId
					if (parentPropertyId !== null) {
						if (!mmpvState.materialPropertyParents.has(id)) {
							// in case the property does not yet exist in the parents store, add it with an empty list
							mmpvState.materialPropertyParents.set(id, [parentPropertyId])
						} else {
							const existing = mmpvState.materialPropertyParents.get(id) ?? []
							if (!existing.includes(parentPropertyId)) {
								mmpvState.materialPropertyParents.set(id, [...existing, parentPropertyId])
							}
						}
					}
				}
				changeToMaterialPropertyListElement(
					data.listElement,
					data.listElementCopy,
					data.parent,
					false
				)
			} else {
				// we are at last prop and thus can close the model, but need to check for material, i.e. if we need to directly register property in material
				// if property was saved with material context, add property to material
				if (materialId !== undefined && updateMaterial !== undefined) {
					updateMaterial(id)
				} else {
					changeUrl('/materialProperties', window)
				}
				modals.close()
			}
		} else {
			// reload property in store from server and refresh breadcrumbs and actual property with copy
			const last = breadcrumbs.get().at(-1)
			if (!last) return // cannot happen, the current property always has a breadcrumb
			const refreshingPropertyId = last.data.materialProperty.id
			if (refreshingPropertyId !== 'new') {
				await reloadProperty(refreshingPropertyId)

				const refreshedProperty = mmpvState.getMaterialProperty(refreshingPropertyId)
				if (!refreshedProperty) return
				last.data.materialProperty = { ...refreshedProperty }
				last.data.materialPropertyCopy = { ...refreshedProperty }
				breadcrumbs.setContext(breadcrumbs.get()) // trigger reactivity after in-place mutation
			}
		}
	}

	async function reloadProperty(propertyId: string) {
		await fetchUtils
			.get(`/api/property/get/${propertyId}`)
			.then((data) => {
				let propertyToUpdate = DefaultMaterialProperty()
				Object.assign(propertyToUpdate, data)
				mmpvState.materialProperties.set(propertyToUpdate.id, propertyToUpdate) // save new property to store
				materialProperty = { ...propertyToUpdate }
				materialPropertyCopy = { ...propertyToUpdate }
			})
			.catch(fetchUtils.catchErrorAndShowNotification())
	}

	/**
	 * Pushes the material property breadcrumb.
	 * Updates the stack top if necessary.
	 * @param {boolean} updateStackTop - Indicate whether to update the stack top.
	 * @param {Object} itemParent - Parent item.
	 * @param {string} breadcrumbTitle - Title for the breadcrumb.
	 */
	function pushMaterialPropertyBreadcrumb(
		updateStackTop: boolean,
		itemParent = parent,
		breadcrumbTitle: string = materialProperty.name
	) {
		// when we push the first item, we already have something about this item in the store, so we need to pop and push again with updated data
		// but we still have to keep the copy from when we first opened the element and not with the changes already
		let copy = { ...materialPropertyCopy }
		if (updateStackTop) {
			// we must pop once to be able to push the updated data
			const top = breadcrumbs.pop()
			if (top) {
				copy = top.data.materialPropertyCopy
			}
		}
		breadcrumbs.push({
			title: breadcrumbTitle,
			data: {
				view: MaterialPropertyView.MATERIAL_PROPERTY,
				materialProperty: { ...materialProperty },
				materialPropertyCopy: copy,
				parent: { ...itemParent }
			},
			callback: (data, index) =>
				changeToMaterialProperty(
					data.materialProperty,
					data.materialPropertyCopy,
					data.parent,
					true,
					index
				)
		})
	}

	/**
	 * Pushes the material property list element breadcrumb.
	 * Updates the stack top if necessary.
	 * @param {boolean} updateStackTop - Indicate whether to update the stack top.
	 * @param itemParent - Parent item.
	 */
	function pushMaterialPropertyListElementBreadcrumb(
		updateStackTop: boolean,
		itemParent = materialProperty
	): void {
		// when we push the first item, we already have something about this item in the store, so we need to pop and push again with updated data
		let copy = { ...propertyElementCopy }
		if (updateStackTop) {
			// we must pop once to be able to push the updated data
			const top = breadcrumbs.pop()
			if (top) {
				copy = top.data.listElementCopy
			}
		}
		breadcrumbs.push({
			title: propertyElement.name, // for list element, we always have the name
			data: {
				view: MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT,
				listElement: { ...propertyElement },
				listElementCopy: copy,
				parent: { ...itemParent }
			},
			callback: (data, index) =>
				changeToMaterialPropertyListElement(
					data.listElement,
					data.listElementCopy,
					data.parent,
					true,
					index
				)
		})
	}

	function pushPropertyAndGoToListElement(listElement: MaterialPropertyListElement) {
		pushMaterialPropertyBreadcrumb(true)
		isNew = false
		propertyElement = { ...DefaultMaterialPropertyListElement(), ...listElement }
		propertyElementCopy = { ...DefaultMaterialPropertyListElement(), ...listElement }
		view = MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT
		updateViewVariables()
		pushMaterialPropertyListElementBreadcrumb(false)
	}

	/**
	 * Pushes and navigates to the property.
	 * @param {MaterialProperty} prop - The property object.
	 * @param {boolean} isNewElement - Indicate whether it is a new element.
	 */
	function pushAndGoToProperty(prop: MaterialProperty, isNewElement: boolean = false) {
		pushMaterialPropertyListElementBreadcrumb(true)
		changeToMaterialProperty(prop, prop, propertyElement, false)
		isNew = isNewElement
		updateViewVariables()
		pushMaterialPropertyBreadcrumb(false, propertyElement, viewHandler.breadcrumbTitle)
	}

	/**
	 * Changes to the material property, either by click on breadcrumb, saving or moving forward/registering new property.
	 * @param {MaterialProperty} prop - The property object.
	 * @param {MaterialProperty} propCopy - Copy of the property object.
	 * @param parentData - Parent data.
	 * @param {boolean} checkChanges - Indicate whether to check changes.
	 * @param {number} breadcrumbIndex - Breadcrumb index.
	 */
	function changeToMaterialProperty(
		prop: MaterialProperty,
		propCopy: MaterialProperty,
		parentData: MaterialPropertyListElement | MaterialProperty | Material,
		checkChanges: boolean = false,
		breadcrumbIndex: number = -1
	) {
		if (checkChanges) {
			let noChangesDetected = handleClose(
				() => changeToMaterialProperty(prop, propCopy, parentData, false, breadcrumbIndex),
				breadcrumbIndex,
				$t('UI.mmpv.materialPropertyModal.unsavedChanges.action.breadcrumb'),
				$t('UI.mmpv.materialPropertyModal.unsavedChanges.button.navigate')
			)
			if (!noChangesDetected) {
				return
			}
			isNew = false // if we go backwards, we do not have a new item
		}

		if (breadcrumbIndex > -1) breadcrumbs.popUntil(breadcrumbIndex)

		view = MaterialPropertyView.MATERIAL_PROPERTY
		materialProperty = { ...prop }
		materialPropertyCopy = { ...propCopy }
		parent = parentData
	}

	/**
	 * Changes to the material property list element.
	 * @param {MaterialPropertyListElement} listElement - The list element object.
	 * @param {MaterialPropertyListElement} listElementCopy - Copy of the list element object.
	 * @param {Object} parentData - Parent data.
	 * @param {boolean} checkChanges - Indicate whether to check changes.
	 * @param {number} breadcrumbIndex - Breadcrumb index.
	 */
	function changeToMaterialPropertyListElement(
		listElement: MaterialPropertyListElement,
		listElementCopy: MaterialPropertyListElement,
		parentData: MaterialPropertyListElement | MaterialProperty | Material,
		checkChanges: boolean = false,
		breadcrumbIndex: number = -1
	) {
		if (checkChanges) {
			let noChangesDetected = handleClose(
				() =>
					changeToMaterialPropertyListElement(
						listElement,
						listElementCopy,
						parentData,
						false,
						breadcrumbIndex
					),
				breadcrumbIndex,
				$t('UI.mmpv.materialPropertyModal.unsavedChanges.action.breadcrumb'),
				$t('UI.mmpv.materialPropertyModal.unsavedChanges.button.navigate')
			)
			if (!noChangesDetected) {
				return
			}
			isNew = false // if we go backwards, we do not have a new item
		}

		if (breadcrumbIndex > -1) {
			breadcrumbs.popUntil(breadcrumbIndex)
		}

		view = MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT
		propertyElement = { ...listElement }
		propertyElementCopy = { ...listElementCopy }
		parent = parentData
	}

	/**
	 * Process breadcrumb data based on the view
	 * @param {*} breadcrumb
	 * @return {Array} - list of element and its copy
	 */
	function processBreadcrumbData(breadcrumb: Breadcrumb) {
		if (breadcrumb.data.view === MaterialPropertyView.MATERIAL_PROPERTY) {
			return [breadcrumb.data.materialProperty, breadcrumb.data.materialPropertyCopy]
		} else if (breadcrumb.data.view === MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT) {
			return [breadcrumb.data.listElement, breadcrumb.data.listElementCopy]
		}
	}

	/**
	 * Handle close action. Gives the overlayFun to the overlay in case there are changes made such that we have the
	 * double-opt to really abort.
	 * Return false if there are any changes made.
	 * @param {Function} overlayFun - Function to execute.
	 * @param {number} checkIndex - Index of breadcrumb to which we would navigate, -1 to close all.
	 * @param {string} actionString - Action string.
	 * @param {string} buttonString - Button label string.
	 * @return {boolean} No changes made.
	 */
	function handleClose(
		overlayFun: () => void,
		checkIndex: number,
		actionString: string,
		buttonString: string = $t('UI.mmpv.materialPropertyModal.unsavedChanges.button.quit')
	): boolean {
		let editedItems: (MaterialProperty | MaterialPropertyListElement)[] = []
		breadcrumbs.get().forEach((breadcrumb, i) => {
			if (i > checkIndex) {
				const breadcrumbData = processBreadcrumbData(breadcrumb)
				if (!breadcrumbData) return // cannot happen, every breadcrumb carries one of the two views
				const [element, elementCopy] = breadcrumbData

				let isUnedited = deepEqual(element, elementCopy)
				if (!isUnedited) {
					editedItems.push(element)
				}
			}
		})
		if (view === MaterialPropertyView.MATERIAL_PROPERTY) {
			const isPropertyUnedited = deepEqual(materialProperty, materialPropertyCopy)
			if (!isPropertyUnedited) {
				editedItems.push(materialProperty)
			}
		} else if (view === MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT) {
			const isListElementUnedited = deepEqual(propertyElement, propertyElementCopy)
			if (!isListElementUnedited) {
				editedItems.push(propertyElement.name !== '' ? propertyElement : propertyElementCopy)
			}
		}
		// display overlay if there are unsaved changes
		if (editedItems.length > 0) {
			showOverlay = true
			let editedItemNames: string[] = []
			editedItems.forEach((item) => {
				editedItemNames.push(
					item.name
						? item.name
						: $t('UI.mmpv.materialPropertyModal.unsavedChanges.newItemWithoutName')
				)
			})

			overlayMessage = $t('UI.mmpv.materialPropertyModal.unsavedChanges.message', {
				list: [
					...new Set(
						editedItemNames.map((name) => "<li style='margin-top: 8px;'>" + name + '</li>')
					)
				].join(''),
				userActionText: actionString
			})
			overlayAction = () => {
				overlayFun()
				showOverlay = false
			}
			overlayButtonLabel = buttonString
			return false
		} else {
			return true
		}
	}

	function switchToNewPropertyOfListElement(listElement: MaterialPropertyListElement) {
		if (listElement !== undefined) {
			pushMaterialPropertyBreadcrumb(true) // push the actual material property
			propertyElement = listElement
			pushMaterialPropertyListElementBreadcrumb(false, materialProperty)
			changeToMaterialProperty(
				DefaultMaterialProperty(),
				DefaultMaterialProperty(),
				propertyElement,
				false
			)
			isNew = true
			updateViewVariables() // to build the correct title, update the view variables
			pushMaterialPropertyBreadcrumb(false, propertyElement, viewHandler.breadcrumbTitle) // push the new property
		}
	}

	/**
	 * Checks for any changes and redirects to the property details page based on the passed in event detail. For now,
	 * this method is called from the tooltip with the info about the parents.
	 *
	 * This function first extracts the property id from the event detail. It then checks
	 * for any changes using handleClose() and if there are changes
	 * or handleClose() returns true, the modal is closed and the user is redirected to the property
	 * page of the specified property id on the same host and protocol.
	 *
	 * @function checkChangesAndRedirectToProperty
	 * @param {Event} event - The custom event object which contains the property id in its detail attribute.
	 */
	function checkChangesAndRedirectToProperty(event: Event) {
		if (!(event instanceof CustomEvent)) return
		const id = String(event.detail)
		const redirect = () => {
			modals.close()
			window.location.href =
				window.location.protocol + '//' + window.location.host + '/materialProperty/' + id
		}
		if (
			handleClose(
				redirect,
				-1,
				$t('UI.mmpv.materialPropertyModal.unsavedChanges.action.toProperty')
			)
		) {
			redirect()
		}
	}

	function closeAndChangeUrlToProperties() {
		// reset the last two visited entities to before the changes
		materialProperty = { ...materialPropertyCopy }
		propertyElement = { ...propertyElementCopy }
		modals.close()
		if (materialId === undefined || updateMaterial === undefined) {
			changeUrl('/materialProperties', window)
		}
	}

	function closeMaterialPropertyModal() {
		if (
			handleClose(
				closeAndChangeUrlToProperties,
				-1,
				$t('UI.mmpv.materialPropertyModal.unsavedChanges.action.close')
			)
		) {
			closeAndChangeUrlToProperties()
		}
	}

	/**
	 * former svelte 4 `$:` watchers. the handler bodies run untracked because they read and
	 * write other reactive state (viewHandler, stores) — tracked, each effect would retrigger
	 * on its own writes. only the `void`-listed dependencies rerun them.
	 */
	$effect(() => {
		void materialProperty
		untrack(() => viewHandler?.buildMaterialPropertyParentsInfoTooltip())
	})
	// on changes to view, isNew or the property name execute updateViewVariables
	$effect(() => {
		void view
		void isNew
		void materialProperty.name
		untrack(() => updateViewVariables())
	})
</script>

{#if isOpen}
	<div
		role="dialog"
		class="modal"
		in:fly|global={{ y: -1000, duration: 400 }}
		out:fly|global={{ y: -1000, duration: 400 }}
	>
		<div class="contents" style="--content-padding: 3.13rem 3.5rem; max-width: 920px; width: 920px">
			<Notifications target="modal" position="absolute" top="0" />
			{#if showCloseIcon}
				<Button
					variant="secondary-icon"
					class="cancel-btn"
					style="--btn-border-width: 0"
					title={$t('UI.button.cancel')}
					onclick={closeMaterialPropertyModal}
				>
					<IconCross width={24} height={24} />
				</Button>
			{/if}
			{#if showOverlay}
				<div class="modal-overlay-content">
					<div>
						<span>{@html overlayTitle}</span>
						<p>{@html overlayMessage}</p>
					</div>
					<div>
						<Button variant="secondary" color="red" onclick={overlayAction}>
							{overlayButtonLabel}
						</Button>

						<Button
							variant="primary"
							color="red"
							onclick={() => {
								showOverlay = false
							}}
						>
							{$t('UI.mmpv.materialPropertyModal.button.continue')}
						</Button>
					</div>
				</div>
			{:else}
				<Breadcrumbs />
				<div class="sub-content-row">
					<div class="material-property-modal-title">
						<h2>{@html viewHandler.title}</h2>
						{#if viewHandler.materialPropertyTooltip !== ''}
							<Tooltip
								placementTtip={TooltipPlacements.RIGHT}
								msg={viewHandler.materialPropertyTooltip}
								isHtml
								showOnCreate
								removeCloseIconAfterClose
								appendToObject="parent"
							/>
						{/if}
					</div>
					<span>{@html viewHandler.description}</span>
				</div>
				{#if view === MaterialPropertyView.MATERIAL_PROPERTY}
					<!-- create a new property -->
					<MaterialPropertyForm
						bind:materialProperty
						bind:this={materialPropertyForm}
						onPushListElementAndCreateNewProperty={switchToNewPropertyOfListElement}
						onPushMaterialProperty={pushPropertyAndGoToListElement}
					/>
				{:else if view === MaterialPropertyView.MATERIAL_PROPERTY_LIST_ELEMENT}
					<!-- change sub-properties of material property list element -->
					<MaterialPropertyListElementForm
						bind:propertyElement
						bind:this={propertyElementForm}
						selectedProperty={materialProperty}
						onAddCreatePropertyBreadcrumb={() =>
							pushAndGoToProperty(DefaultMaterialProperty(), true)}
						onEditMaterialProperty={(property) => pushAndGoToProperty(property)}
					/>
				{/if}
				<div class="actions">
					<Button variant="secondary" color="grey" onclick={closeMaterialPropertyModal}>
						{$t('UI.button.cancel')}
					</Button>
					<Button variant="primary" color="green" onclick={save} disabled={acceptDisabled}>
						<IconCheckmark width={20} height={20} />
						{$t('UI.button.save')}
					</Button>
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	@import '$styles/modals.scss';

	h2 {
		width: auto;
		margin: 0;
	}

	div.material-property-modal-title {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-start;
		gap: 8px;

		width: 100%;
	}

	.actions {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-end;
		gap: 6px;

		width: 100%;
		border-radius: 0 0 6px 6px;
		margin: 0;
	}
</style>
